import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Trash,
  PencilSimple,
  DownloadSimple,
  PaperPlaneTilt,
  UploadSimple,
  Check,
  Buildings,
  ArrowClockwise,
  X as XIcon,
  IdentificationCard,
  CreditCard,
  Camera,
  UserPlus,
  Sparkle,
  PenNib,
  Copy,
} from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { openDownload, metFirma } from "@/lib/downloadUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  bepaalde_voltijds: "Bepaalde duur · Voltijds",
  bepaalde_deeltijds: "Bepaalde duur · Deeltijds",
  onbepaalde_voltijds: "Onbepaalde duur · Voltijds",
  onbepaalde_deeltijds: "Onbepaalde duur · Deeltijds",
  vervanging: "Vervangingscontract",
  student: "Studentencontract",
  flexi: "Flexi-job",
  stage: "Stage",
};

const STATUS_BADGES = {
  concept: { label: "Concept", color: "#94A3B8", bg: "#F1F5F9" },
  actief: { label: "Actief", color: "#16A34A", bg: "#F0FAEC" },
  verlopen: { label: "Verlopen", color: "#DC2626", bg: "#FEF2F2" },
  beeindigd: { label: "Beëindigd", color: "#525252", bg: "#F5F5F5" },
};

const DAGNAMEN = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

const fmtDateNL = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtIsoToday = () => new Date().toISOString().slice(0, 10);

const emptyContract = (medewerker_id = "", filiaal_id = "") => ({
  medewerker_id,
  filiaal_id,
  type: "bepaalde_voltijds",
  functie: "Arbeider",
  taakomschrijving: "Algemene Hulp",
  datum_van: fmtIsoToday(),
  datum_tot: "",
  uren_per_week: 38,
  werkdagen: [0, 1, 2, 3, 4],
  uur_van: "10:00",
  uur_tot: "18:00",
  pauze_min: 60,
  bruto_per_uur: 0,
  bruto_per_maand: 0,
  paritair_comite: "100.00",
  iban_werknemer: "",
  bic_werknemer: "",
  proefperiode_dagen: 0,
  extra_clausules: "",
});

export default function Contracten() {
  const navigate = useNavigate();
  const location = useLocation();
  const [contracten, setContracten] = useState([]);
  const [medewerkers, setMedewerkers] = useState([]);
  const [filialen, setFilialen] = useState([]);
  const [bedrijf, setBedrijf] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const [fMed, setFMed] = useState("all");
  const [fFil, setFFil] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fType, setFType] = useState("all");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // contract object (incl id) of null
  const [bedrijfDialog, setBedrijfDialog] = useState(false);
  const [mailDialog, setMailDialog] = useState(null); // { contract }
  const [medDocumenten, setMedDocumenten] = useState([]);

  // Documenten van de in het formulier gekozen medewerker
  useEffect(() => {
    const medId = editing?.medewerker_id;
    if (!medId) { setMedDocumenten([]); return; }
    let actief = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/documenten`, { params: { medewerker_id: medId } })
      .then((r) => { if (actief) setMedDocumenten(r.data); })
      .catch(() => { if (actief) setMedDocumenten([]); });
    return () => { actief = false; };
  }, [editing?.medewerker_id]);

  const [uploadDialog, setUploadDialog] = useState(null); // { contract }
  const [newMedDialog, setNewMedDialog] = useState(false);
  const [newMedForm, setNewMedForm] = useState(null); // {voornaam, achternaam, ...}
  const [scanBusy, setScanBusy] = useState(false);
  const [creatingMed, setCreatingMed] = useState(false);
  const [signDialog, setSignDialog] = useState(null); // { contract, werknemerSig, werkgeverSig, plaats }
  const [signSaving, setSignSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m, f, b] = await Promise.all([
        axios.get(`${API}/contracten`),
        axios.get(`${API}/medewerkers`),
        axios.get(`${API}/filialen`),
        axios.get(`${API}/contracten/bedrijf`),
      ]);
      setContracten(c.data || []);
      setMedewerkers(m.data || []);
      setFilialen(f.data || []);
      setBedrijf(b.data || null);
    } catch {
      toast.error("Kon data niet laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-open contract-dialog wanneer ?nieuwe-voor=<med_id> in URL staat
  useEffect(() => {
    if (medewerkers.length === 0) return;
    const params = new URLSearchParams(location.search);
    const medId = params.get("nieuwe-voor");
    if (medId && medewerkers.find((m) => m.id === medId)) {
      const med = medewerkers.find((m) => m.id === medId);
      setEditing({ ...emptyContract(medId) });
      setDialogOpen(true);
      setTimeout(() => fillFromMedewerker(med), 0);
      // URL opschonen zodat het bij herladen niet opnieuw triggert
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medewerkers.length]);

  const filtered = contracten.filter((c) => {
    if (fMed !== "all" && c.medewerker_id !== fMed) return false;
    if (fFil !== "all" && c.filiaal_id !== fFil) return false;
    if (fStatus !== "all" && c.status !== fStatus) return false;
    if (fType !== "all" && c.type !== fType) return false;
    return true;
  });

  const openNew = () => {
    setEditing({ ...emptyContract() });
    setDialogOpen(true);
  };

  // Vult contract-velden vanuit een geselecteerde medewerker
  const fillFromMedewerker = (med) => {
    if (!med) return;
    setEditing((prev) => ({
      ...(prev || emptyContract()),
      medewerker_id: med.id,
      iban_werknemer: med.iban || prev?.iban_werknemer || "",
      bic_werknemer: med.bic || prev?.bic_werknemer || "",
      bruto_per_uur: parseFloat(med.uurloon) || prev?.bruto_per_uur || 0,
      functie: med.functie || prev?.functie || "Arbeider",
      filiaal_id: (med.filiaal_ids && med.filiaal_ids[0]) || prev?.filiaal_id || "",
    }));
  };

  const openNewMed = () => {
    setNewMedForm({
      voornaam: "",
      achternaam: "",
      email: "",
      telefoon: "",
      adres: "",
      rijksregisternr: "",
      geboortedatum: "",
      nationaliteit: "Belgisch",
      iban: "",
      bic: "",
      uurloon: "",
      eid_kaartnummer: "",
      eid_geldig_tot: "",
    });
    setNewMedDialog(true);
  };

  const scanEid = async (files) => {
    if (!files || files.length === 0) return;
    setScanBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("images", f));
      const res = await axios.post(`${API}/contracten/scan-eid`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      if (!d.ok) {
        toast.error("Geen leesbare gegevens gevonden — probeer een scherpere foto");
        return;
      }
      setNewMedForm((prev) => ({
        ...(prev || {}),
        voornaam: d.voornaam || prev?.voornaam || "",
        achternaam: d.achternaam || prev?.achternaam || "",
        adres: d.adres || prev?.adres || "",
        rijksregisternr: d.rijksregisternr || prev?.rijksregisternr || "",
        geboortedatum: d.geboortedatum || prev?.geboortedatum || "",
        nationaliteit: d.nationaliteit || prev?.nationaliteit || "Belgisch",
        eid_kaartnummer: d.kaartnummer || prev?.eid_kaartnummer || "",
        eid_geldig_tot: d.geldig_tot || prev?.eid_geldig_tot || "",
      }));
      const filled = [d.voornaam, d.achternaam, d.adres, d.rijksregisternr].filter(Boolean).length;
      toast.success(`${filled} velden ingevuld via eID-scan`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "eID-scan mislukt");
    } finally {
      setScanBusy(false);
    }
  };

  const scanBank = async (files) => {
    if (!files || files.length === 0) return;
    setScanBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("images", f));
      const res = await axios.post(`${API}/contracten/scan-bank`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      if (!d.ok) {
        toast.error("Geen IBAN gevonden — probeer een scherpere foto");
        return;
      }
      setNewMedForm((prev) => ({
        ...(prev || {}),
        iban: d.iban || prev?.iban || "",
        bic: d.bic || prev?.bic || "",
      }));
      toast.success("IBAN ingevuld via bankkaart-scan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bankkaart-scan mislukt");
    } finally {
      setScanBusy(false);
    }
  };

  const saveNewMed = async () => {
    if (!newMedForm.voornaam || !newMedForm.achternaam) {
      toast.error("Voor- en achternaam zijn verplicht");
      return;
    }
    setCreatingMed(true);
    try {
      const payload = {
        ...newMedForm,
        uurloon: parseFloat(newMedForm.uurloon || 0),
        verlof_recht: 20,
        filiaal_ids: [],
      };
      const res = await axios.post(`${API}/medewerkers`, payload);
      const created = res.data;
      toast.success(`Medewerker ${created.voornaam} ${created.achternaam} aangemaakt`);
      // Refresh medewerkers + selecteer in contract-dialog
      const medsRes = await axios.get(`${API}/medewerkers`);
      setMedewerkers(medsRes.data || []);
      fillFromMedewerker(created);
      setNewMedDialog(false);
      setNewMedForm(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setCreatingMed(false);
    }
  };

  // ── Tablet-tekenen ──────────────────────────────────────────────────────
  const openSignDialog = (c) => {
    setSignDialog({
      contract: c,
      werknemerSig: null,
      werkgeverSig: null,
      plaats: bedrijf?.stad?.split(" ").slice(1).join(" ").toUpperCase() || "",
    });
  };
  const submitSign = async () => {
    if (!signDialog?.werknemerSig) {
      toast.error("Werknemer-handtekening ontbreekt");
      return;
    }
    setSignSaving(true);
    try {
      await axios.post(`${API}/contracten/${signDialog.contract.id}/teken-tablet`, {
        handtekening_dataurl: signDialog.werknemerSig,
        werkgever_handtekening_dataurl: signDialog.werkgeverSig || "",
        plaats: signDialog.plaats || "",
      });
      toast.success("Contract digitaal getekend ✍️");
      setSignDialog(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tekenen mislukt");
    } finally {
      setSignSaving(false);
    }
  };

  const openEdit = (c) => {
    setEditing({ ...c });
    setDialogOpen(true);
  };

  const duplicateContract = (c) => {
    // Open contract-dialog met alle velden gekopieerd, behalve datum/status/PDFs
    const today = fmtIsoToday();
    let nextEnd = "";
    // Bereken nieuwe einddatum op basis van originele duur (in dagen)
    if (c.datum_van && c.datum_tot) {
      try {
        const oud_van = new Date(c.datum_van);
        const oud_tot = new Date(c.datum_tot);
        const diffMs = oud_tot - oud_van;
        if (diffMs > 0) {
          const newEnd = new Date(new Date(today).getTime() + diffMs);
          nextEnd = newEnd.toISOString().slice(0, 10);
        }
      } catch {
        nextEnd = "";
      }
    }
    setEditing({
      ...c,
      id: undefined,
      datum_van: today,
      datum_tot: nextEnd,
      status: "concept",
      pdf_pad: "",
      getekende_pdf_pad: "",
      verstuurd_op: "",
      getekend_op: "",
      handtekening_pad: "",
      bijna_verlopen: false,
      created_at: undefined,
      updated_at: undefined,
    });
    setDialogOpen(true);
    toast.success(`Kopie van contract — datums + status geleegd, alle andere gegevens behouden`);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const saveContract = async () => {
    if (!editing.medewerker_id) { toast.error("Kies een medewerker"); return; }
    if (!editing.datum_van) { toast.error("Kies een startdatum"); return; }
    try {
      const payload = { ...editing };
      // Stuur alleen velden die mogen
      delete payload.id;
      delete payload.medewerker_naam;
      delete payload.filiaal_naam;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.pdf_pad;
      delete payload.getekende_pdf_pad;
      delete payload.verstuurd_op;
      delete payload.getekend_op;
      delete payload.bijna_verlopen;

      if (editing.id) {
        await axios.put(`${API}/contracten/${editing.id}`, payload);
        toast.success("Contract bijgewerkt");
      } else {
        await axios.post(`${API}/contracten`, payload);
        toast.success("Contract aangemaakt");
      }
      closeDialog();
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    }
  };

  const deleteContract = async (c) => {
    try {
      await axios.delete(`${API}/contracten/${c.id}`);
      toast.success("Verwijderd");
      loadAll();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const downloadPdf = (c) => {
    openDownload(`${API}/contracten/${c.id}/pdf`);
  };

  const saveBedrijf = async () => {
    try {
      await axios.put(`${API}/contracten/bedrijf`, bedrijf);
      toast.success("Bedrijfsgegevens opgeslagen");
      setBedrijfDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    }
  };

  const sendMail = async () => {
    if (!mailDialog) return;    try {
      await axios.post(`${API}/contracten/${mailDialog.contract.id}/verstuur`, {
        bericht: mailDialog.bericht || "",
        cc: mailDialog.cc || null,
        document_ids: mailDialog.document_ids || [],
      });
      toast.success("Contract verstuurd per e-mail");
      setMailDialog(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verzenden mislukt");
    }
  };

  const openMailDialog = async (contract) => {    setMailDialog({ contract, bericht: "", cc: "", document_ids: [], documenten: [] });
    try {
      const res = await axios.get(`${API}/documenten`, { params: { medewerker_id: contract.medewerker_id } });
      setMailDialog((d) => (d && d.contract.id === contract.id ? { ...d, documenten: res.data } : d));
    } catch {
      /* geen rechten of geen documenten */
    }
  };

  const uploadGetekend = async (file) => {
    if (!uploadDialog || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/contracten/${uploadDialog.contract.id}/upload-getekend`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Getekende versie geüpload");
      setUploadDialog(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload mislukt");
    }
  };

  return (
    <div className="space-y-5" data-testid="contracten-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <FileText size={24} weight="bold" className="text-[#002FA7]" />
            Arbeidscontracten
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Maak Belgisch-conforme arbeidsovereenkomsten aan en stuur ze direct naar je medewerkers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={loadAll} title="Vernieuwen" data-testid="refresh-btn">
            <ArrowClockwise size={16} weight="bold" />
          </Button>
          <Button variant="outline" onClick={() => setBedrijfDialog(true)} data-testid="bedrijf-btn">
            <Buildings size={16} weight="bold" className="mr-1" /> Bedrijfsgegevens
          </Button>
          <Button onClick={openNew} className="bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="new-contract-btn">
            <Plus size={16} weight="bold" className="mr-1" /> Nieuw contract
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white border border-border rounded-sm p-3" data-testid="filters">
        <div>
          <Label className="text-xs font-semibold">Medewerker</Label>
          <Select value={fMed} onValueChange={setFMed}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-med"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle medewerkers</SelectItem>
              {medewerkers.map((m) => <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Filiaal</Label>
          <Select value={fFil} onValueChange={setFFil}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-fil"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle filialen</SelectItem>
              {filialen.map((f) => <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Type</Label>
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Status</Label>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="mt-1.5 h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statussen</SelectItem>
              {Object.entries(STATUS_BADGES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Lijst */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Laden...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-12 text-center">
          <FileText size={32} weight="thin" className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Nog geen contracten. Klik op "Nieuw contract" om te beginnen.</p>
        </div>
      ) : (
        <>
        <div className="mob-list" data-testid="contracten-mobiel">
          {filtered.map((c) => {
            const badge = STATUS_BADGES[c.status] || STATUS_BADGES.concept;
            return (
              <div key={c.id} className="mob-card" data-testid={`contract-kaart-${c.id}`}>
                <div className="mob-card-head">
                  <div className="min-w-0">
                    <p className="mob-title">{c.medewerker_naam}</p>
                    <p className="text-xs text-muted-foreground truncate">{TYPE_LABELS[c.type] || c.type}{c.filiaal_naam ? ` · ${c.filiaal_naam}` : ""}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider shrink-0" style={{ color: badge.color, backgroundColor: badge.bg }}>
                    {badge.label}
                  </span>
                </div>
                <div className="mob-row mt-1.5">
                  <span className="mob-label">Periode</span>
                  <span className="mob-value font-mono">{fmtDateNL(c.datum_van)} {c.datum_tot ? `→ ${fmtDateNL(c.datum_tot)}` : "(onbepaald)"}</span>
                </div>
                <div className="mob-row">
                  <span className="mob-label">Uren / bruto</span>
                  <span className="mob-value">{c.uren_per_week}u/w · € {Number(c.bruto_per_uur || 0).toFixed(2).replace(".", ",")}</span>
                </div>
                {c.bijna_verlopen && c.status === "actief" && <p className="text-[10px] text-[#F59E0B] font-bold mt-1">⚠ binnenkort verlopen</p>}
                {c.getekend_op && <p className="text-[10px] text-[#16A34A] font-bold mt-1">✓ getekend</p>}
                <div className="flex flex-wrap justify-end gap-1 mt-2 pt-2 border-t border-border">
                  <button onClick={() => downloadPdf(c)} className="w-9 h-9 flex items-center justify-center text-[#002FA7] border border-border rounded-sm" data-testid={`mob-pdf-${c.id}`}><DownloadSimple size={15} weight="bold" /></button>
                  <button onClick={() => openMailDialog(c)} className="w-9 h-9 flex items-center justify-center text-[#002FA7] border border-border rounded-sm" data-testid={`mob-mail-${c.id}`}><PaperPlaneTilt size={15} weight="bold" /></button>
                  <button onClick={() => openSignDialog(c)} className="w-9 h-9 flex items-center justify-center text-[#16A34A] border border-border rounded-sm" data-testid={`mob-sign-${c.id}`}><PenNib size={15} weight="bold" /></button>
                  <button onClick={() => setUploadDialog({ contract: c })} className="w-9 h-9 flex items-center justify-center text-[#002FA7] border border-border rounded-sm" data-testid={`mob-upload-${c.id}`}><UploadSimple size={15} weight="bold" /></button>
                  <button onClick={() => openEdit(c)} className="w-9 h-9 flex items-center justify-center text-[#002FA7] border border-border rounded-sm" data-testid={`mob-edit-${c.id}`}><PencilSimple size={15} weight="bold" /></button>
                  <button onClick={() => duplicateContract(c)} className="w-9 h-9 flex items-center justify-center text-[#002FA7] border border-border rounded-sm" data-testid={`mob-dup-${c.id}`}><Copy size={15} weight="bold" /></button>
                  <button onClick={() => deleteContract(c)} className="w-9 h-9 flex items-center justify-center text-[#DC2626] border border-border rounded-sm" data-testid={`mob-del-${c.id}`}><Trash size={15} weight="bold" /></button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="hidden md:block bg-white border border-border rounded-sm overflow-x-auto" data-testid="contracten-list">
          <table className="w-full text-xs">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Medewerker</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Type</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">Filiaal</th>
                <th className="px-3 py-2 text-center font-bold uppercase tracking-wider text-muted-foreground">Periode</th>
                <th className="px-3 py-2 text-center font-bold uppercase tracking-wider text-muted-foreground">Uren</th>
                <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground">Bruto/u</th>
                <th className="px-3 py-2 text-center font-bold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground">Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const badge = STATUS_BADGES[c.status] || STATUS_BADGES.concept;
                return (
                  <tr key={c.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-${c.id}`}>
                    <td className="px-3 py-2 font-semibold">{c.medewerker_naam}</td>
                    <td className="px-3 py-2">{TYPE_LABELS[c.type] || c.type}</td>
                    <td className="px-3 py-2">{c.filiaal_naam || "—"}</td>
                    <td className="px-3 py-2 text-center font-mono">
                      {fmtDateNL(c.datum_van)}<br />
                      <span className="text-muted-foreground">{c.datum_tot ? `→ ${fmtDateNL(c.datum_tot)}` : "(onbepaald)"}</span>
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums">{c.uren_per_week}u/w</td>
                    <td className="px-3 py-2 text-right tabular-nums">€ {Number(c.bruto_per_uur || 0).toFixed(4).replace(".", ",")}</td>
                    <td className="px-3 py-2 text-center">
                      <span
                        className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: badge.color, backgroundColor: badge.bg }}
                      >
                        {badge.label}
                      </span>
                      {c.bijna_verlopen && c.status === "actief" && (
                        <div className="text-[10px] text-[#F59E0B] font-bold mt-0.5">⚠ binnenkort verlopen</div>
                      )}
                      {c.verstuurd_op && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">📤 {fmtDateNL(c.verstuurd_op.slice(0, 10))}</div>
                      )}
                      {c.getekend_op && (
                        <div className="text-[10px] text-[#16A34A] font-bold mt-0.5">✓ getekend</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="flex justify-end gap-0.5">
                        <button onClick={() => downloadPdf(c)} title="Download PDF" className="w-7 h-7 flex items-center justify-center text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm" data-testid={`pdf-${c.id}`}>
                          <DownloadSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => openMailDialog(c)} title="Verstuur per e-mail" className="w-7 h-7 flex items-center justify-center text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm" data-testid={`mail-${c.id}`}>
                          <PaperPlaneTilt size={14} weight="bold" />
                        </button>
                        <button onClick={() => openSignDialog(c)} title="Teken op tablet" className="w-7 h-7 flex items-center justify-center text-[#16A34A] hover:bg-[#F0FAEC] rounded-sm" data-testid={`sign-${c.id}`}>
                          <PenNib size={14} weight="bold" />
                        </button>
                        <button onClick={() => setUploadDialog({ contract: c })} title="Upload getekende versie" className="w-7 h-7 flex items-center justify-center text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm" data-testid={`upload-${c.id}`}>
                          <UploadSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => openEdit(c)} title="Bewerken" className="w-7 h-7 flex items-center justify-center text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm" data-testid={`edit-${c.id}`}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => duplicateContract(c)} title="Dupliceren als nieuw contract" className="w-7 h-7 flex items-center justify-center text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm" data-testid={`dup-${c.id}`}>
                          <Copy size={14} weight="bold" />
                        </button>
                        <button onClick={() => deleteContract(c)} title="Verwijderen" className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:bg-[#FEF2F2] hover:text-[#DC2626] rounded-sm" data-testid={`del-${c.id}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* ─── Contract Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl p-0 max-h-[92vh] flex flex-col gap-0" data-testid="contract-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>{editing?.id ? "Contract bewerken" : "Nieuw arbeidscontract"}</DialogTitle>
            <DialogDescription>
              Vul de gegevens in. De PDF wordt automatisch gegenereerd op basis van Belgische standaard-clausules.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <Tabs defaultValue="basis" className="w-full flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid grid-cols-3 mx-6 mt-4 shrink-0">
                <TabsTrigger value="basis" data-testid="tab-basis">Basis</TabsTrigger>
                <TabsTrigger value="uren" data-testid="tab-uren">Uren & Loon</TabsTrigger>
                <TabsTrigger value="extra" data-testid="tab-extra">Extra</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto px-6 py-4">
              <TabsContent value="basis" className="space-y-3 mt-0">
                <div>
                  <Label className="text-xs font-semibold">Medewerker *</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Select value={editing.medewerker_id} onValueChange={(v) => {
                      const m = medewerkers.find((x) => x.id === v);
                      if (m) fillFromMedewerker(m);
                      else setEditing({ ...editing, medewerker_id: v });
                    }}>
                      <SelectTrigger className="flex-1 h-9" data-testid="ed-med"><SelectValue placeholder="Kies..." /></SelectTrigger>
                      <SelectContent>
                        {medewerkers.map((m) => <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={openNewMed}
                      title="Nieuwe medewerker aanmaken (eID/bankkaart-scan mogelijk)"
                      className="border-[#002FA7] text-[#002FA7] hover:bg-[#EAF1FF]"
                      data-testid="new-med-btn"
                    >
                      <UserPlus size={16} weight="bold" className="mr-1" /> Nieuw
                    </Button>
                  </div>
                </div>
                {editing.medewerker_id && (
                  <div className="border border-border rounded-sm p-2 bg-muted/20" data-testid="contract-med-documenten">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                      📎 Documenten van deze medewerker ({medDocumenten.length})
                    </p>
                    {medDocumenten.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground italic">
                        Nog niets bewaard — voeg documenten toe bij Medewerkers → Bewerken → Documenten.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {medDocumenten.map((d) => (
                          <a
                            key={d.id}
                            href={metFirma(`${API}/documenten/${d.id}/bestand`)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] px-2 py-1 border border-[#002FA7]/40 rounded-sm text-[#002FA7] hover:bg-[#EAF1FF] font-semibold"
                            title={`${d.type_label}${d.geldig_tot ? ` · geldig tot ${new Date(d.geldig_tot).toLocaleDateString("nl-BE")}` : ""}`}
                            data-testid={`contract-doc-${d.id}`}
                          >
                            {d.titel}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold">Filiaal (vestigingseenheid)</Label>
                  <Select value={editing.filiaal_id || "_geen"} onValueChange={(v) => setEditing({ ...editing, filiaal_id: v === "_geen" ? "" : v })}>
                    <SelectTrigger className="mt-1.5 h-9" data-testid="ed-fil"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_geen">— geen specifieke vestiging —</SelectItem>
                      {filialen.map((f) => <SelectItem key={f.id} value={f.id}>{f.naam}{f.vestigingseenheid ? ` (${f.vestigingseenheid})` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Contracttype *</Label>
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger className="mt-1.5 h-9" data-testid="ed-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Functie</Label>
                    <Input value={editing.functie} onChange={(e) => setEditing({ ...editing, functie: e.target.value })} className="mt-1.5 h-9" data-testid="ed-functie" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Taakomschrijving</Label>
                    <Input value={editing.taakomschrijving} onChange={(e) => setEditing({ ...editing, taakomschrijving: e.target.value })} className="mt-1.5 h-9" data-testid="ed-taak" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Startdatum *</Label>
                    <Input type="date" value={editing.datum_van} onChange={(e) => setEditing({ ...editing, datum_van: e.target.value })} className="mt-1.5 h-9" data-testid="ed-van" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Einddatum (leeg = onbepaald)</Label>
                    <Input type="date" value={editing.datum_tot} onChange={(e) => setEditing({ ...editing, datum_tot: e.target.value })} className="mt-1.5 h-9" data-testid="ed-tot" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="uren" className="space-y-3 mt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Uren per week</Label>
                    <Input type="number" step="0.5" value={editing.uren_per_week} onChange={(e) => setEditing({ ...editing, uren_per_week: parseFloat(e.target.value) || 0 })} className="mt-1.5 h-9" data-testid="ed-uren" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Pauze (minuten)</Label>
                    <Input type="number" value={editing.pauze_min} onChange={(e) => setEditing({ ...editing, pauze_min: parseInt(e.target.value) || 0 })} className="mt-1.5 h-9" data-testid="ed-pauze" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Werkdagen</Label>
                  <div className="flex gap-1 mt-1.5">
                    {DAGNAMEN.map((n, i) => {
                      const active = (editing.werkdagen || []).includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            const set = new Set(editing.werkdagen || []);
                            if (set.has(i)) set.delete(i); else set.add(i);
                            setEditing({ ...editing, werkdagen: Array.from(set).sort() });
                          }}
                          className={`flex-1 py-2 text-xs font-bold rounded-sm border transition-colors ${
                            active ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-muted-foreground border-border hover:border-[#002FA7]"
                          }`}
                          data-testid={`ed-dag-${i}`}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Uur van</Label>
                    <Input type="time" value={editing.uur_van} onChange={(e) => setEditing({ ...editing, uur_van: e.target.value })} className="mt-1.5 h-9" data-testid="ed-uvan" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Uur tot</Label>
                    <Input type="time" value={editing.uur_tot} onChange={(e) => setEditing({ ...editing, uur_tot: e.target.value })} className="mt-1.5 h-9" data-testid="ed-utot" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Bruto per uur (€)</Label>
                    <Input type="number" step="0.0001" value={editing.bruto_per_uur} onChange={(e) => setEditing({ ...editing, bruto_per_uur: parseFloat(e.target.value) || 0 })} className="mt-1.5 h-9" data-testid="ed-bruto-uur" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Paritair Comité</Label>
                    <Input value={editing.paritair_comite} onChange={(e) => setEditing({ ...editing, paritair_comite: e.target.value })} className="mt-1.5 h-9" data-testid="ed-pc" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">IBAN werknemer</Label>
                    <Input value={editing.iban_werknemer} onChange={(e) => setEditing({ ...editing, iban_werknemer: e.target.value })} placeholder="BE.. .. .. .." className="mt-1.5 h-9" data-testid="ed-iban" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">BIC</Label>
                    <Input value={editing.bic_werknemer} onChange={(e) => setEditing({ ...editing, bic_werknemer: e.target.value })} className="mt-1.5 h-9" data-testid="ed-bic" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="extra" className="space-y-3 mt-0">
                <div>
                  <Label className="text-xs font-semibold">Proefperiode (kalenderdagen)</Label>
                  <Input type="number" value={editing.proefperiode_dagen} onChange={(e) => setEditing({ ...editing, proefperiode_dagen: parseInt(e.target.value) || 0 })} className="mt-1.5 h-9" data-testid="ed-proef" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Extra clausules / bijzondere bepalingen</Label>
                  <Textarea
                    value={editing.extra_clausules}
                    onChange={(e) => setEditing({ ...editing, extra_clausules: e.target.value })}
                    rows={6}
                    placeholder="Eén regel per clausule. Worden onderaan het contract toegevoegd als Artikel 11."
                    className="mt-1.5 text-sm"
                    data-testid="ed-extra"
                  />
                </div>
                {editing.id && (
                  <div>
                    <Label className="text-xs font-semibold">Status</Label>
                    <Select value={editing.status || "concept"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger className="mt-1.5 h-9" data-testid="ed-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_BADGES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </TabsContent>
              </div>
            </Tabs>
          )}

          <DialogFooter className="px-6 py-3 border-t border-border bg-muted/30 shrink-0">
            <Button variant="outline" onClick={closeDialog} data-testid="dlg-cancel">
              <XIcon size={14} weight="bold" className="mr-1" /> Annuleren
            </Button>
            <Button onClick={saveContract} className="bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="dlg-save">
              <Check size={14} weight="bold" className="mr-1" /> Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Bedrijf Dialog ─── */}
      <Dialog open={bedrijfDialog} onOpenChange={setBedrijfDialog}>
        <DialogContent className="max-w-xl" data-testid="bedrijf-dialog">
          <DialogHeader>
            <DialogTitle>Bedrijfsgegevens</DialogTitle>
            <DialogDescription>Deze gegevens verschijnen op alle gegenereerde contracten.</DialogDescription>
          </DialogHeader>
          {bedrijf && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Naam</Label>
                  <Input value={bedrijf.naam || ""} onChange={(e) => setBedrijf({ ...bedrijf, naam: e.target.value })} className="mt-1.5 h-9" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Vertegenwoordiger</Label>
                  <Input value={bedrijf.vertegenwoordiger || ""} onChange={(e) => setBedrijf({ ...bedrijf, vertegenwoordiger: e.target.value })} className="mt-1.5 h-9" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Adres</Label>
                  <Input value={bedrijf.adres || ""} onChange={(e) => setBedrijf({ ...bedrijf, adres: e.target.value })} className="mt-1.5 h-9" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Stad (postcode + naam)</Label>
                  <Input value={bedrijf.stad || ""} onChange={(e) => setBedrijf({ ...bedrijf, stad: e.target.value })} placeholder="2020 ANTWERPEN" className="mt-1.5 h-9" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">KBO-nummer</Label>
                  <Input value={bedrijf.kbo || ""} onChange={(e) => setBedrijf({ ...bedrijf, kbo: e.target.value })} className="mt-1.5 h-9" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">RSZ-nummer</Label>
                  <Input value={bedrijf.rsz || ""} onChange={(e) => setBedrijf({ ...bedrijf, rsz: e.target.value })} className="mt-1.5 h-9" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Paritair Comité</Label>
                  <Input value={bedrijf.paritair_comite || ""} onChange={(e) => setBedrijf({ ...bedrijf, paritair_comite: e.target.value })} className="mt-1.5 h-9" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Sector</Label>
                  <Input value={bedrijf.sector || ""} onChange={(e) => setBedrijf({ ...bedrijf, sector: e.target.value })} className="mt-1.5 h-9" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Telefoon</Label>
                  <Input value={bedrijf.telefoon || ""} onChange={(e) => setBedrijf({ ...bedrijf, telefoon: e.target.value })} className="mt-1.5 h-9" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">E-mail</Label>
                  <Input value={bedrijf.email || ""} onChange={(e) => setBedrijf({ ...bedrijf, email: e.target.value })} className="mt-1.5 h-9" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBedrijfDialog(false)}>Annuleren</Button>
            <Button onClick={saveBedrijf} className="bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="bedrijf-save">
              <Check size={14} weight="bold" className="mr-1" /> Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Mail Dialog ─── */}
      <Dialog open={!!mailDialog} onOpenChange={(o) => !o && setMailDialog(null)}>
        <DialogContent data-testid="mail-dialog">
          <DialogHeader>
            <DialogTitle>Contract verzenden</DialogTitle>
            <DialogDescription>
              Naar: <b>{mailDialog?.contract.medewerker_naam}</b>{" "}
              {medewerkers.find((m) => m.id === mailDialog?.contract.medewerker_id)?.email && (
                <>· <span className="font-mono">{medewerkers.find((m) => m.id === mailDialog?.contract.medewerker_id)?.email}</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Persoonlijk bericht (optioneel)</Label>
              <Textarea
                value={mailDialog?.bericht || ""}
                onChange={(e) => setMailDialog({ ...mailDialog, bericht: e.target.value })}
                rows={4}
                placeholder="Beste, hierbij vind je je arbeidsovereenkomst..."
                className="mt-1.5 text-sm"
                data-testid="mail-bericht"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">CC (optioneel)</Label>
              <Input
                value={mailDialog?.cc || ""}
                onChange={(e) => setMailDialog({ ...mailDialog, cc: e.target.value })}
                placeholder="kopie@bedrijf.be"
                className="mt-1.5 h-9"
                data-testid="mail-cc"
              />
            </div>
            <div data-testid="mail-documenten">
              <Label className="text-xs font-semibold">Documenten meesturen</Label>
              {(mailDialog?.documenten || []).length === 0 ? (
                <p className="text-xs text-muted-foreground italic mt-1">
                  Geen documenten bewaard voor deze medewerker.
                </p>
              ) : (
                <div className="mt-1.5 border border-border rounded-sm divide-y divide-border max-h-40 overflow-y-auto">
                  {mailDialog.documenten.map((d) => {
                    const gekozen = (mailDialog.document_ids || []).includes(d.id);
                    return (
                      <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-muted/40" data-testid={`mail-doc-${d.id}`}>
                        <input
                          type="checkbox"
                          checked={gekozen}
                          onChange={(e) =>
                            setMailDialog((prev) => ({
                              ...prev,
                              document_ids: e.target.checked
                                ? [...(prev.document_ids || []), d.id]
                                : (prev.document_ids || []).filter((x) => x !== d.id),
                            }))
                          }
                          data-testid={`mail-doc-check-${d.id}`}
                        />
                        <span className="flex-1 truncate">
                          <span className="font-semibold">{d.titel}</span>
                          <span className="text-muted-foreground"> · {d.type_label}</span>
                        </span>
                        <a
                          href={metFirma(`${API}/documenten/${d.id}/bestand`)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#002FA7] hover:underline text-[10px] font-bold"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`mail-doc-open-${d.id}`}
                        >
                          OPEN
                        </a>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMailDialog(null)}>Annuleren</Button>
            <Button onClick={sendMail} className="bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="mail-send">
              <PaperPlaneTilt size={14} weight="bold" className="mr-1" /> Versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Nieuwe Medewerker Dialog (met eID + bankkaart scan) ─── */}
      <Dialog open={newMedDialog} onOpenChange={(o) => !o && setNewMedDialog(false)}>
        <DialogContent className="max-w-xl p-0 max-h-[92vh] flex flex-col gap-0" data-testid="new-med-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} weight="bold" className="text-[#002FA7]" />
              Nieuwe medewerker
            </DialogTitle>
            <DialogDescription>
              Scan de eID en bankkaart om de gegevens automatisch in te vullen, of vul handmatig aan.
            </DialogDescription>
          </DialogHeader>

          {newMedForm && (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="new-med-body">
              {/* Scan-knoppen bovenaan */}
              <div className="grid grid-cols-2 gap-3">
                <label className={`flex flex-col items-center justify-center gap-1.5 py-3 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
                  scanBusy ? "border-muted bg-muted opacity-50 pointer-events-none" : "border-[#002FA7] bg-[#EAF1FF] hover:bg-[#D9E5FF]"
                }`} data-testid="scan-eid-label">
                  <IdentificationCard size={26} weight="duotone" className="text-[#002FA7]" />
                  <span className="text-xs font-bold uppercase tracking-wider text-[#002FA7] flex items-center gap-1">
                    <Sparkle size={11} weight="fill" /> Scan eID
                  </span>
                  <span className="text-[10px] text-muted-foreground">Voor + achterzijde</span>
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    capture="environment"
                    className="hidden"
                    onChange={(e) => scanEid(e.target.files)}
                    data-testid="scan-eid-input"
                  />
                </label>
                <label className={`flex flex-col items-center justify-center gap-1.5 py-3 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
                  scanBusy ? "border-muted bg-muted opacity-50 pointer-events-none" : "border-[#16A34A] bg-[#F0FAEC] hover:bg-[#E0F4D8]"
                }`} data-testid="scan-bank-label">
                  <CreditCard size={26} weight="duotone" className="text-[#16A34A]" />
                  <span className="text-xs font-bold uppercase tracking-wider text-[#16A34A] flex items-center gap-1">
                    <Sparkle size={11} weight="fill" /> Scan bankkaart
                  </span>
                  <span className="text-[10px] text-muted-foreground">IBAN + BIC</span>
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    capture="environment"
                    className="hidden"
                    onChange={(e) => scanBank(e.target.files)}
                    data-testid="scan-bank-input"
                  />
                </label>
              </div>
              {scanBusy && (
                <div className="px-3 py-2 bg-[#FFF8E1] border border-[#F59E0B]/40 rounded-sm text-xs text-[#92400E] flex items-center gap-2">
                  <Camera size={14} weight="bold" className="animate-pulse" />
                  AI leest de foto's... dit kan enkele seconden duren.
                </div>
              )}

              {/* Form-velden */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Voornaam *</Label>
                  <Input value={newMedForm.voornaam} onChange={(e) => setNewMedForm({ ...newMedForm, voornaam: e.target.value })} className="mt-1.5 h-9" data-testid="nm-voornaam" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Achternaam *</Label>
                  <Input value={newMedForm.achternaam} onChange={(e) => setNewMedForm({ ...newMedForm, achternaam: e.target.value })} className="mt-1.5 h-9" data-testid="nm-achternaam" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Adres</Label>
                <Input value={newMedForm.adres} onChange={(e) => setNewMedForm({ ...newMedForm, adres: e.target.value })} placeholder="Straat 1, 0000 Stad" className="mt-1.5 h-9" data-testid="nm-adres" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Rijksregisternr</Label>
                  <Input value={newMedForm.rijksregisternr} onChange={(e) => setNewMedForm({ ...newMedForm, rijksregisternr: e.target.value })} placeholder="00.00.00-000.00" className="mt-1.5 h-9" data-testid="nm-rr" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Geboortedatum</Label>
                  <Input type="date" value={newMedForm.geboortedatum} onChange={(e) => setNewMedForm({ ...newMedForm, geboortedatum: e.target.value })} className="mt-1.5 h-9" data-testid="nm-geboorte" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Nationaliteit</Label>
                  <Input value={newMedForm.nationaliteit} onChange={(e) => setNewMedForm({ ...newMedForm, nationaliteit: e.target.value })} className="mt-1.5 h-9" data-testid="nm-nat" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Telefoon</Label>
                  <Input value={newMedForm.telefoon} onChange={(e) => setNewMedForm({ ...newMedForm, telefoon: e.target.value })} className="mt-1.5 h-9" data-testid="nm-tel" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">E-mail</Label>
                <Input type="email" value={newMedForm.email} onChange={(e) => setNewMedForm({ ...newMedForm, email: e.target.value })} className="mt-1.5 h-9" data-testid="nm-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">IBAN</Label>
                  <Input value={newMedForm.iban} onChange={(e) => setNewMedForm({ ...newMedForm, iban: e.target.value })} placeholder="BE.. .... .... ...." className="mt-1.5 h-9 font-mono text-xs" data-testid="nm-iban" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">BIC</Label>
                  <Input value={newMedForm.bic} onChange={(e) => setNewMedForm({ ...newMedForm, bic: e.target.value })} className="mt-1.5 h-9 font-mono text-xs" data-testid="nm-bic" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Bruto uurloon (€) — optioneel</Label>
                <Input type="number" step="0.0001" value={newMedForm.uurloon} onChange={(e) => setNewMedForm({ ...newMedForm, uurloon: e.target.value })} className="mt-1.5 h-9" data-testid="nm-uurloon" />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                <div>
                  <Label className="text-xs font-semibold">eID kaartnummer</Label>
                  <Input value={newMedForm.eid_kaartnummer || ""} onChange={(e) => setNewMedForm({ ...newMedForm, eid_kaartnummer: e.target.value })} className="mt-1.5 h-9 font-mono text-xs" data-testid="nm-eid-nr" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">eID geldig tot</Label>
                  <Input type="date" value={newMedForm.eid_geldig_tot || ""} onChange={(e) => setNewMedForm({ ...newMedForm, eid_geldig_tot: e.target.value })} className="mt-1.5 h-9" data-testid="nm-eid-tot" />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="px-6 py-3 border-t border-border bg-muted/30 shrink-0">
            <Button variant="outline" onClick={() => { setNewMedDialog(false); setNewMedForm(null); }}>Annuleren</Button>
            <Button onClick={saveNewMed} disabled={creatingMed} className="bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="nm-save">
              <Check size={14} weight="bold" className="mr-1" />
              {creatingMed ? "Aanmaken..." : "Aanmaken & gebruiken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Sign Dialog (Tablet) ─── */}
      <Dialog open={!!signDialog} onOpenChange={(o) => !o && setSignDialog(null)}>
        <DialogContent className="max-w-3xl p-0 max-h-[95vh] flex flex-col gap-0" data-testid="sign-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <PenNib size={20} weight="bold" className="text-[#16A34A]" />
              Digitaal ondertekenen
            </DialogTitle>
            <DialogDescription>
              <b>{signDialog?.contract.medewerker_naam}</b> · {TYPE_LABELS[signDialog?.contract.type]} · start {fmtDateNL(signDialog?.contract.datum_van)}
              {signDialog?.contract.datum_tot && <> → eind {fmtDateNL(signDialog?.contract.datum_tot)}</>}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {/* Plaats */}
            <div>
              <Label className="text-xs font-semibold">Plaats van ondertekening</Label>
              <Input
                value={signDialog?.plaats || ""}
                onChange={(e) => setSignDialog({ ...signDialog, plaats: e.target.value })}
                placeholder="ANTWERPEN"
                className="mt-1.5 h-9 max-w-xs"
                data-testid="sign-plaats"
              />
            </div>

            {/* Werknemer signature */}
            <div className="border border-border rounded-sm p-4 bg-[#FAFAFA]">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#002FA7] mb-2 flex items-center gap-1.5">
                <PenNib size={14} weight="bold" /> Handtekening werknemer
                {signDialog?.werknemerSig && <Check size={14} weight="fill" className="text-[#16A34A]" />}
              </h3>
              {signDialog?.werknemerSig ? (
                <div className="flex items-start gap-3">
                  <img src={signDialog.werknemerSig} alt="handtekening werknemer" className="border border-border rounded-sm bg-white max-h-24" />
                  <Button variant="outline" size="sm" onClick={() => setSignDialog({ ...signDialog, werknemerSig: null })} data-testid="reset-werknemer-sig">
                    <ArrowClockwise size={12} weight="bold" className="mr-1" /> Opnieuw tekenen
                  </Button>
                </div>
              ) : (
                <SignaturePad
                  label="Werknemer tekent met de vinger of pen"
                  onConfirm={(d) => setSignDialog({ ...signDialog, werknemerSig: d })}
                />
              )}
            </div>

            {/* Werkgever signature */}
            <div className="border border-border rounded-sm p-4 bg-[#FAFAFA]">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#002FA7] mb-2 flex items-center gap-1.5">
                <PenNib size={14} weight="bold" /> Handtekening werkgever <span className="text-muted-foreground font-normal normal-case">(optioneel)</span>
                {signDialog?.werkgeverSig && <Check size={14} weight="fill" className="text-[#16A34A]" />}
              </h3>
              {signDialog?.werkgeverSig ? (
                <div className="flex items-start gap-3">
                  <img src={signDialog.werkgeverSig} alt="handtekening werkgever" className="border border-border rounded-sm bg-white max-h-24" />
                  <Button variant="outline" size="sm" onClick={() => setSignDialog({ ...signDialog, werkgeverSig: null })} data-testid="reset-werkgever-sig">
                    <ArrowClockwise size={12} weight="bold" className="mr-1" /> Opnieuw tekenen
                  </Button>
                </div>
              ) : (
                <SignaturePad
                  label="Werkgever / vertegenwoordiger tekent"
                  onConfirm={(d) => setSignDialog({ ...signDialog, werkgeverSig: d })}
                />
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-3 border-t border-border bg-muted/30 shrink-0">
            <Button variant="outline" onClick={() => setSignDialog(null)}>Annuleren</Button>
            <Button
              onClick={submitSign}
              disabled={!signDialog?.werknemerSig || signSaving}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white"
              data-testid="sign-submit"
            >
              <Check size={14} weight="bold" className="mr-1" />
              {signSaving ? "Bezig met opslaan..." : "Bevestigen & PDF genereren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Upload Dialog ─── */}
      <Dialog open={!!uploadDialog} onOpenChange={(o) => !o && setUploadDialog(null)}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Getekende versie uploaden</DialogTitle>
            <DialogDescription>
              Voor: <b>{uploadDialog?.contract.medewerker_naam}</b> — alleen PDF.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Input
              type="file"
              accept=".pdf"
              onChange={(e) => { if (e.target.files?.[0]) uploadGetekend(e.target.files[0]); }}
              data-testid="upload-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(null)}>Annuleren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
